import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";

import MediaAPI from "../../../api/MediaAPI";
import { getTranslatedMetadata } from "../../../utils/data/Content";
import { isFastNowSmartlist } from "../../../utils/typePredicate";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  CarouselWithDataSourceParams,
  MosaicWithDataSourceParams,
  NowComponentParams,
} from "../../../api/ProjectAPI.types";
import type { AppState } from "../../../reducers";

interface ThemeOption {
  value: string;
  label: string;
}

type ComponentWithFastNowDataSource =
  | CarouselWithDataSourceParams<NowComponentParams>
  | MosaicWithDataSourceParams<NowComponentParams>;

interface FastNowSelectProps {
  onChange: (selectedOption: SingleValue<ThemeOption>) => void;
  item: ComponentWithFastNowDataSource;
}

export default function FastNowSelect({
  onChange,
  item,
}: FastNowSelectProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const languageId = useSelector(
    (state: AppState) => state.languages.current.uuid,
  );

  const getMediasMetadataListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediasMetadataList,
    params: [],
  });

  const { data: metadataData } = useQuery(getMediasMetadataListQuery);

  const themeList = metadataData?.themes.map((theme) => ({
    label: getTranslatedMetadata(theme, "title", languageId),
    value: theme.uuid,
  }));

  const themeListOptions = themeList && [
    {
      label: t("allThemes"),
      value: "all",
    },
    ...themeList,
  ];

  const themeSelected = isFastNowSmartlist(item)
    ? themeListOptions?.find((theme) => theme.value === item.params.themeId)
    : null;
  return (
    <div className="editor-form-row">
      <div className="editor-form-label">{t("theme")}</div>
      <div className="editor-form-input" style={{ maxWidth: "400px" }}>
        <Select
          className="editor-select"
          classNamePrefix="slct"
          name="form-field-name"
          onChange={onChange}
          options={themeListOptions}
          placeholder={t("allThemes")}
          value={themeSelected}
        />
      </div>
    </div>
  );
}
